import React from 'react';
import { Lightbulb, X } from 'lucide-react';
import { useAuthStatus } from '@/hooks/useApi';

/**
 * Help plan phase 4: a one-line tip the first time someone opens a page, inline (never an overlay or a pop-up), with
 * "Got it". Dismissed per user in this browser (best effort); "Show tips again" in the user menu brings them all back.
 */
const PREFIX = 'mp.tips';
const keyOf = (user: string, id: string) => `${PREFIX}.${user}.${id}`;
export const TIPS_RESET_EVENT = 'mp-tips-reset';

export function showTipsAgain(user: string): number {
  let n = 0;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(`${PREFIX}.${user}.`)) { window.localStorage.removeItem(k); n++; }
    }
  } catch { /* not kept */ }
  window.dispatchEvent(new Event(TIPS_RESET_EVENT));
  return n;
}

export const FirstUseTip: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { data: auth } = useAuthStatus();
  const user = auth?.user ? String(auth.user.id ?? auth.user.username) : '';
  const [gone, setGone] = React.useState(true);
  React.useEffect(() => {
    const read = () => {
      if (!user) return;
      try { setGone(window.localStorage.getItem(keyOf(user, id)) === '1'); } catch { setGone(false); }
    };
    read();
    window.addEventListener(TIPS_RESET_EVENT, read);
    return () => window.removeEventListener(TIPS_RESET_EVENT, read);
  }, [user, id]);
  if (!user || gone) return null;
  const dismiss = () => {
    setGone(true);
    try { window.localStorage.setItem(keyOf(user, id), '1'); } catch { /* not kept: gone for this visit */ }
  };
  return (
    <div role="note" data-testid={`tip-${id}`}
         className="flex items-start gap-mp-2 rounded-mp-sm border border-mp-border bg-mp-info-bg px-mp-3 py-mp-2 text-mp-sm text-mp-text">
      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-mp-accent" aria-hidden />
      <span className="min-w-0 flex-1">{children}</span>
      <button type="button" onClick={dismiss} className="shrink-0 text-mp-accent hover:underline">Got it</button>
      <button type="button" onClick={dismiss} aria-label="Dismiss this tip" className="shrink-0 text-mp-text-muted hover:text-mp-text">
        <X className="h-3.5 w-3.5" aria-hidden /></button>
    </div>
  );
};

export default FirstUseTip;
